import clsx from 'clsx';
import type * as React from 'react';

import { useMergedRefs } from '@gitbook/util-react-hooks';

import { useFieldContext } from './FieldContext';
import { useFieldController } from './FieldControllerContext';

type FieldProps = {
    onChange?: (...event: any[]) => void;
    onBlur?: (...event: any[]) => void;
    id?: string;
    value?: any;
    name?: string;
    disabled?: boolean | undefined;
    'aria-invalid'?: React.AriaAttributes['aria-invalid'];
    'aria-labelledby'?: React.AriaAttributes['aria-labelledby'];
    'aria-describedby'?: React.AriaAttributes['aria-describedby'];
};

/**
 * Get props for a field.
 */
export function useFieldProps<Props extends FieldProps & { ref?: React.Ref<any> }>(
    props: Props
): FieldProps & { ref: React.Ref<any> } & Props {
    const fieldContext = useFieldContext();
    const controller = useFieldController();
    const ref = useMergedRefs(controller?.field.ref, props.ref);
    if (!fieldContext) {
        return { ...props, ref };
    }
    const { controlId: id, labelId, errorId, descriptionId } = fieldContext;
    const contextProps = {
        ...props,
        ref,
        id,
        'aria-labelledby': clsx(labelId, props['aria-labelledby']) || undefined,
        'aria-describedby': clsx(errorId, descriptionId, props['aria-describedby']) || undefined,
    };
    if (!controller) {
        return contextProps;
    }
    return {
        ...contextProps,
        onChange: (...events) => {
            controller.field.onChange(...events);
            props.onChange?.(...events);
        },
        onBlur: (...events) => {
            controller.field.onBlur();
            props.onBlur?.(...events);
        },
        value: controller.field.value,
        name: controller.field.name,
        disabled: controller.field.disabled || props.disabled,
        'aria-invalid': controller.fieldState.invalid || props['aria-invalid'],
    };
}

type AriaFieldProps = Omit<FieldProps, 'aria-invalid' | 'disabled'> & {
    isInvalid?: boolean | undefined;
    isDisabled?: boolean | undefined;
};

export function useAriaFieldProps<Props extends AriaFieldProps & { ref?: React.Ref<any> }>(
    props: Props
): AriaFieldProps & { ref: React.Ref<any> } & Props {
    const {
        'aria-invalid': isInvalid,
        disabled: isDisabled,
        ...fieldProps
    } = useFieldProps({
        ...props,
        'aria-invalid': props.isInvalid,
        disabled: props.isDisabled,
    });
    return {
        ...fieldProps,
        isInvalid,
        isDisabled,
    } as AriaFieldProps & { ref: React.Ref<any> } & Props;
}
